import { Fragment as _Fragment, jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import { useRef, useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { Button } from '../Button';
import { apihost, vpmobile, vptiny, humsize, muniname } from '../../../utils';
import './FileList.scss';
import * as API from '../../API';
export const FileList = ({ className, title, knr, year, linkToArchivePage = false, recentFiles = false }) => {
    const [mostRecentYear, setMostRecentYear] = useState(null);
    useEffect(() => {
        API.mostRecentYear()
            .then(mry => setMostRecentYear((mry || -1) + 1));
    }, []);
    const infoRef = useRef(null);
    const archiveRef = useRef(null);
    const infoCacheId = `kb/kommuner/${mostRecentYear}/${knr}`;
    const { data: municipalityInfoData } = useQuery(infoCacheId, () => API.fetchMunicipalityInfo(knr !== null && knr !== void 0 ? knr : ''), { enabled: !!knr });
    const archiveCacheId = year || recentFiles ? `kb/filarkiv/${knr}/${recentFiles ? mostRecentYear : year}` : `kb/filarkiv/${knr}`;
    const { data } = useQuery(archiveCacheId, () => API.fetchFileArchiveSet(knr !== null && knr !== void 0 ? knr : '', recentFiles ? undefined : year, recentFiles));
    if (municipalityInfoData) {
        infoRef.current = municipalityInfoData;
    }
    if (data) {
        archiveRef.current = data !== null && data !== void 0 ? data : null;
    }
    const municipality_name = muniname(infoRef.current);
    let jsxArchiveItems = [];
    let finalVersionExists = false;
    const description = (file, timestamp) => {
        const updateInfo = timestamp
            ? ` Tallene ble oppdatert ${timestamp}.`
            : ``;
        switch (file.versjon) {
            case 'endelig':
                finalVersionExists = true;
                return vpmobile() ? _jsxs(_Fragment, { children: ["Den klassiske rapporten med tabeller og analyser, ", file.rapportaar] })
                    : _jsxs(_Fragment, { children: ["Den klassiske rapporten med tabeller og analyser, siste gjeldende versjon av Kommunebarometeret ", file.rapportaar, " for ", municipality_name, ". ", updateInfo] });
            case 'foreløpig':
                return vpmobile() ? _jsxs(_Fragment, { children: ["Forel\u00F8pig utgave av barometeret ", file.rapportaar] })
                    : _jsxs(_Fragment, { children: ["Forel\u00F8pig utgave av Kommunebarometeret for ", file.rapportaar, ", ", municipality_name, ". Dette er en tidligere utgave som ", finalVersionExists ? 'er' : 'blir', " erstattet av endelig versjon. ", updateInfo] });
            case 'tabelloversikt':
                return vpmobile() ? _jsxs(_Fragment, { children: ["Utskriftsvennlig versjon av barometeret for ", file.rapportaar] })
                    : _jsxs(_Fragment, { children: ["En kortere og utskriftsvennlig versjon av Kommunebarometeret for ", file.rapportaar, ". Oversikten inneholder kun tabellene innen hver sektor, uten analysetekster og annet innhold. ", updateInfo] });
            case 'formler':
                return vpmobile() ? _jsxs(_Fragment, { children: ["Utregninger og formler for samtlige n\u00F8kkeltall, ", file.rapportaar] })
                    : _jsxs(_Fragment, { children: ["Alle utregningene med kildehenvisninger for samtlige n\u00F8kkeltall som gjelder ", municipality_name, " i Kommunebarometeret ", file.rapportaar, ". ", updateInfo] });
            case 'masterfil':
                return vpmobile() ? _jsxs(_Fragment, { children: ["Alle n\u00F8kkeltall for alle kommuner, ", file.rapportaar] })
                    : _jsxs(_Fragment, { children: ["Excel-fil med alle n\u00F8kkeltall for alle kommuner i Kommune\u00ADbarometeret ", file.rapportaar, ". Her kan du selv sjekke resultatene fra siste kj\u00F8ring. ", updateInfo] });
            case 'sektorrangeringer':
                return vpmobile() ? _jsxs(_Fragment, { children: ["Sektorrangeringen for alle komnmunene, ", file.rapportaar] })
                    : _jsxs(_Fragment, { children: ["Excel-fil med sektorrangeringene for Kommune\u00ADbarometeret ", file.rapportaar, ". Her ser du hvilken plass alle kommunene ligger p\u00E5 i samtlige sektorer. ", updateInfo] });
            case 'totalrangering':
                return vpmobile() ? _jsxs(_Fragment, { children: ["Totalrangeringen for alle komnmunene, ", file.rapportaar] })
                    : _jsxs(_Fragment, { children: ["Excel-fil med totalrangeringen for Kommune\u00ADbarometeret ", file.rapportaar, ". Her ser du hvilken plass alle kommunene ligger p\u00E5 totalt. ", updateInfo] });
            case 'framtid':
                return vpmobile() ? _jsxs(_Fragment, { children: ["Fremskrivininger, Kommunebarometeret ", file.rapportaar, "."] })
                    : _jsxs(_Fragment, { children: ["Det s\u00E5kalte Framtidsbarometeret for ", municipality_name, ". Fremskrivininger av noen av tallene fra Kommunebarometeret for ", file.rapportaar, ". ", updateInfo] });
            case 'vedlegg':
                return vpmobile() ? _jsxs(_Fragment, { children: ["Vedlegg til Kommunebarometeret ", file.rapportaar, "."] })
                    : _jsxs(_Fragment, { children: ["Filvedlegg til Kommunebarometeret for ", file.rapportaar, ". ", updateInfo] });
            default:
                return vpmobile() ? _jsxs(_Fragment, { children: ["Vedlegg til Kommunebarometeret ", file.rapportaar, "."] })
                    : _jsxs(_Fragment, { children: ["Filvedlegg til Kommunebarometeret for ", file.rapportaar, ". ", updateInfo] });
        }
    };
    const download = (e) => {
        const href = e.currentTarget.getAttribute('href');
        e.preventDefault();
        e.stopPropagation();
        if (href) {
            API.download(href);
        }
    };
    if (archiveRef.current) {
        for (const aar in archiveRef.current) {
            let jsxFiles = [];
            archiveRef.current[aar].forEach((file) => {
                const timestamp = file.oppdatert
                    ? new Intl.DateTimeFormat('nb-NO', { dateStyle: 'long' }).format(new Date(file.oppdatert))
                    : null;
                const href = `${apihost()}/kb/download?file=${file.id_fil}`;
                let title = file.versjon === 'foreløpig' ? 'Foreløpig rapport' : file.dokumenttype;
                if (vptiny()) {
                    title = title.replace('Fremtidsbarometeret', 'Fremt.bar.');
                    title = title.replace('Økonomibarometeret', 'Øk.bar.');
                    title = title.replace('rapport', 'rapp.');
                    title = title.replace('barometer', 'bar.');
                    title = title.replace('Foreløpig', 'Forel.');
                    title = title.replace('Hovedregneark', 'Hovedark');
                    title = title.replace('Sektorrangering', 'Sektorrang.');
                    title = title.replace('Totalrangering', 'Totalrang.');
                }
                else if (timestamp) {
                    title += `, ${timestamp}`;
                }
                jsxFiles.push(_jsxs("li", { children: [_jsxs("div", { className: "file-item", children: [_jsx("span", { className: "type-of-document", children: title }), _jsxs("span", { className: "document-name", children: [_jsx("a", { className: `file-type-${file.filtype}`, onClick: download, href: href, children: file.navn }), " ", _jsxs("em", { children: ["(", humsize(file.lengde), ")"] })] }), _jsx("span", { className: "document-description", children: description(file, timestamp) })] }), _jsx("div", { className: "file-download", children: _jsx(Button, { onClick: download, href: href, title: file.navn, children: "Last ned" }) })] }, `archive-file-${file.id_fil}`));
            });
            const listTitle = title ? title.replace('{aar}', aar) : aar;
            jsxArchiveItems.push(_jsxs("li", { children: [_jsx("h2", { className: "year", children: listTitle }), _jsx("ul", { children: jsxFiles })] }, `archive-year-${aar}`));
        }
        jsxArchiveItems.reverse();
    }
    return (_jsx("div", { className: `file-list ${className}`, children: _jsxs("div", { className: "page-dim no-gutter", children: [_jsx("ul", { className: "files", children: jsxArchiveItems }), _jsx(Button, { className: "archive-page-link", defaultWidth: true, centered: true, show: !!(knr && linkToArchivePage), href: `#kommuner/${knr}/filarkiv`, children: "Filarkivet" })] }) }));
};
